/**
 * CountrySwitcher — Figma `Country/Switcher — open` (node 37:7558).
 *
 * Measured: a 232×32 control (`bg/surface`, 1.5px `accent/indigo` when open,
 * `radius/sm`, pl-6/pr-8) holding a `Mono/12` code chip in `accent/indigo-soft`,
 * the entity name in `Body/14-Med`, and a chevron. The dropdown is 459px wide,
 * `radius/md`, p-6, 2px gaps, with `0 8px 24px rgba(28,31,46,.14)`; each row is
 * px-8/py-6 `radius/sm`, code chip + `Body/13` name (ellipsised), a tick on the
 * current one; locked rows show the name in `text/muted` and the words
 * "No access"; then a `border/default` hairline and the note
 * "Switching changes which country's records you see."
 *
 * ===========================================================================
 * PLACEMENT IS A DOCUMENTED JUDGMENT
 * ===========================================================================
 * In Figma this component is a FLOATING OVERLAY over a full-page scrim at
 * (873, 12) — a demonstration of the open state, not a slot in `Shell/TopBar`
 * (12:982), which has no country control at all. So the design pins the
 * control's SIZE and its VERTICAL position (32px tall at y=12 inside a 56px
 * topbar is exactly centred) but leaves its horizontal anchor undetermined.
 * It is placed at the left of the topbar beside the breadcrumb: the breadcrumb
 * says where you are in the app, and the country says whose records you are
 * looking at, so the two belong together.
 *
 * ===========================================================================
 * SWITCHING DROPS ANY RECORD ID, ON PURPOSE
 * ===========================================================================
 * Carrying the full path across a switch would turn
 * `/countries/IN/inquiries/42` into `/countries/AE/inquiries/42` — a request the
 * API will (correctly) refuse with 403 `COUNTRY_SCOPE_VIOLATION`, because inquiry
 * 42 belongs to India. The switch therefore keeps the MODULE segment and drops
 * everything after it, so the user lands on the same kind of screen in the new
 * country rather than on an error.
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COUNTRIES, COUNTRY_ORDER } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';
import { cn } from '../../lib/cn';

export interface CountrySwitcherProps {
  current: CountryCode;
  accessible: readonly CountryCode[];
}

/** `/countries/IN/inquiries/42` → `inquiries`; `/countries/IN` → ``. */
function moduleSegment(pathname: string): string {
  const parts = pathname.split('/').filter((part) => part !== '');
  // ['countries', 'IN', 'inquiries', '42'] → parts[2]
  return parts[2] ?? '';
}

export function CountrySwitcher({
  current,
  accessible,
}: CountrySwitcherProps): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const currentProfile = COUNTRIES[current];
  const allowed = new Set<CountryCode>(accessible);

  // One accessible country: the control would be a dropdown with a single row
  // that cannot change anything. Show the country, do not pretend it is a choice.
  if (accessible.length < 2) {
    return (
      <div className="flex h-8 items-center gap-2 rounded-sm border border-line bg-surface pl-1.5 pr-2">
        <span className="inline-flex rounded-[6px] bg-indigo-soft px-1.5 py-0.5 font-mono text-mono text-indigo">
          {current}
        </span>
        <span className="max-w-[168px] truncate text-body font-medium text-fg">
          {currentProfile.legalEntity}
        </span>
      </div>
    );
  }

  const onSelect = (code: CountryCode): void => {
    if (code === current) return;
    const segment = moduleSegment(location.pathname);
    navigate(segment === '' ? `/countries/${code}` : `/countries/${code}/${segment}`);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          'flex h-8 w-[232px] items-center justify-between gap-2 rounded-sm border border-line bg-surface pl-1.5 pr-2',
          'data-[state=open]:border-[1.5px] data-[state=open]:border-indigo',
        )}
        aria-label={`Current company: ${currentProfile.legalEntity}. Switch company.`}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="inline-flex shrink-0 rounded-[6px] bg-indigo-soft px-1.5 py-0.5 font-mono text-mono text-indigo">
            {current}
          </span>
          <span className="truncate text-body font-medium text-fg">
            {currentProfile.legalEntity}
          </span>
        </span>
        <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 flex w-[459px] max-w-[calc(100vw-2rem)] flex-col gap-0.5 rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
        >
          {COUNTRY_ORDER.map((code) => {
            const profile = COUNTRIES[code];
            const locked = !allowed.has(code);
            const isCurrent = code === current;
            return (
              <DropdownMenu.Item
                key={code}
                disabled={locked}
                onSelect={() => onSelect(code)}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 outline-none',
                  isCurrent ? 'bg-indigo-soft' : 'bg-surface',
                  locked ? 'cursor-not-allowed' : 'data-[highlighted]:bg-subtle',
                )}
              >
                <span
                  className={cn(
                    'inline-flex shrink-0 rounded-[6px] px-1.5 py-0.5 font-mono text-mono',
                    isCurrent
                      ? 'bg-indigo-soft text-indigo'
                      : 'bg-subtle text-fg-muted dark:ring-1 dark:ring-line',
                  )}
                >
                  {code}
                </span>
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate text-body13',
                    locked ? 'text-fg-muted' : 'text-fg',
                  )}
                >
                  {profile.legalEntity}
                </span>
                {locked ? (
                  <span className="shrink-0 text-label text-fg-muted">No access</span>
                ) : isCurrent ? (
                  <Check aria-hidden="true" className="size-3.5 shrink-0 text-indigo" />
                ) : null}
              </DropdownMenu.Item>
            );
          })}

          <div aria-hidden="true" className="my-0.5 h-px w-full bg-line" />
          <p className="px-2 pb-1 pt-2 text-label text-fg-muted">
            Switching changes which country&rsquo;s records you see.
          </p>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
